import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import OnboardingSummary from '../../components/OnboardingSummary';
import TimesheetDownload from '../../components/TimesheetDownload';
import { meetingsAPI, onboardingAPI, performanceAPI, punchAPI, tasksAPI } from '../../services/api';
import { fmtDate, fmtTime } from '../../utils/dateUtils';
import useRefreshOnFocus from '../../utils/useRefreshOnFocus';
import { taskSourceShort } from '../../utils/taskSource';

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [punch, setPunch] = useState(null);
  const [newEnquiries, setNewEnquiries] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punchLoading, setPunchLoading] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState('lunch_break');

  const loadPunch = () => punchAPI.today().then(({ data }) => setPunch(data)).catch(() => setPunch(null));

  const refresh = () => {
    Promise.all([performanceAPI.me(), tasksAPI.list({ mine: true }), loadPunch()])
      .then(([s, t]) => { setStats(s.data); setRecent(t.data.slice(0, 5)); })
      .finally(() => setLoading(false));
    onboardingAPI.list({ stage: 'tutor_suggestion' })
      .then(({ data }) => setNewEnquiries(data.length)).catch(() => {});
    meetingsAPI.list()
      .then(({ data }) => setMeetings(data.filter((m) => m.status === 'scheduled')))
      .catch(() => {});
    performanceAPI.today().then(({ data }) => setToday(data)).catch(() => {});
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRefreshOnFocus(refresh);

  const handlePunchIn = async () => {
    setPunchLoading(true);
    try { await punchAPI.punchIn({}); await loadPunch(); } finally { setPunchLoading(false); }
  };

  const handlePunchOut = async (reason, endOfDay) => {
    setPunchLoading(true);
    try {
      await punchAPI.punchOut({ reason, end_of_day: !!endOfDay });
      setShowReason(false);
      await loadPunch();
    } finally { setPunchLoading(false); }
  };

  if (loading) return <div className="page-loading">Loading dashboard...</div>;

  const statusColor = { pending: 'orange', in_progress: 'blue', completed: 'green', overdue: 'red', cancelled: 'gray' };
  const priorityColor = { low: 'green', medium: 'blue', high: 'orange', urgent: 'red' };

  const fmtT = (v) => (v ? fmtTime(v) : null);
  const punchedIn = !!punch?.is_punched_in;
  const dayEnded = !!punch?.is_ended;

  return (
    <div className="page">
      <div className="page-header">
        <h2>My Dashboard</h2>
        <Link to="/employee/tasks/new" className="btn btn-primary">+ New Task</Link>
      </div>

      {/* Punch In/Out (multiple sessions per day) */}
      <div className="card mb-6" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>Today's Attendance</strong>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {punch?.punch_in ? `Start: ${fmtT(punch.punch_in)}` : 'Not punched in'}
              {punch?.punch_out ? ` · Last out: ${fmtT(punch.punch_out)}` : ''}
              {punch?.total_hours != null ? ` · ${punch.total_hours.toFixed(2)}h total` : ''}
              {punchedIn ? ' · 🟢 Currently in' : ''}
            </div>
          </div>
          {!dayEnded && !punchedIn && (
            <button className="btn btn-success" onClick={handlePunchIn} disabled={punchLoading}>
              {punchLoading ? '...' : '✅ Punch In'}
            </button>
          )}
          {!dayEnded && punchedIn && !showReason && (
            <button className="btn btn-danger" onClick={() => setShowReason(true)} disabled={punchLoading}>🔴 Punch Out</button>
          )}
          {dayEnded && <span className="badge badge-green">Day Complete · {punch?.total_hours?.toFixed(2)}h</span>}
        </div>

        {showReason && punchedIn && !dayEnded && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>Reason for punch out</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="lunch_break">Lunch Break</option>
                <option value="official_work">Official Work</option>
                <option value="personal_break">Personal Break</option>
                <option value="client_visit">Client Visit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <button className="btn btn-danger" onClick={() => handlePunchOut(reason, false)} disabled={punchLoading}>Punch Out</button>
            <button className="btn btn-primary" onClick={() => handlePunchOut(reason, true)} disabled={punchLoading}>End of Day</button>
            <button className="btn btn-ghost" onClick={() => setShowReason(false)} disabled={punchLoading}>Cancel</button>
          </div>
        )}

        {punch?.sessions?.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12 }} className="text-muted">
            {punch.sessions.map((s, i) => (
              <span key={s.id} style={{ marginRight: 12 }}>
                #{i + 1} {fmtT(s.punch_in)}–{s.punch_out ? fmtT(s.punch_out) : '…'}{s.reason ? ` (${s.reason.replace(/_/g, ' ')})` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      <TimesheetDownload />

      {/* Upcoming Meetings */}
      {meetings.length > 0 && (
        <div className="card mb-6">
          <div className="card-header"><h3>Upcoming Meetings</h3></div>
          <div style={{ padding: '4px 0' }}>
            {meetings.map((m) => (
              <div key={m.id} className="issue-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{m.title} <span className="badge badge-blue ml-2">Upcoming</span></div>
                  {m.meeting_datetime && (
                    <div className="text-muted" style={{ fontSize: 12 }}>📅 {fmtDate(m.meeting_datetime)}</div>
                  )}
                  {m.agenda && <div className="text-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{m.agenda}</div>}
                </div>
                {m.meeting_link && (
                  <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">🔗 Join Meeting</a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <StatCard title="New Enquiries" value={newEnquiries} icon="🎓" color="teal"
          onClick={() => navigate('/onboarding?stage=tutor_suggestion')} />
        <StatCard title="Total Tasks" value={stats?.total_tasks} icon="📋" color="blue" />
        <StatCard title="Completed" value={stats?.completed} icon="✅" color="green" />
        <StatCard title="In Progress" value={stats?.in_progress} icon="🔄" color="orange" />
        <StatCard title="Overdue" value={stats?.overdue} icon="⏰" color="red" />
        <StatCard title="Avg Hours" value={stats?.average_actual_hours != null ? stats.average_actual_hours.toFixed(1) : 'N/A'} icon="⏱" color="purple" />
        <StatCard title="Efficiency" value={stats?.efficiency_percent != null ? `${stats.efficiency_percent}%` : 'N/A'} icon="📈" color="teal" />
        <StatCard
          title="Today's Efficiency"
          value={today?.efficiency_percent != null ? `${today.efficiency_percent}%` : 'N/A'}
          subtitle={today ? `${today.completed} done · ${today.pending} pending` : undefined}
          icon="🎯" color="green" />
      </div>

      <div className="card mt-6">
        <div className="card-header">
          <h3>Recent Tasks</h3>
          <Link to="/employee/tasks" className="link-sm">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="empty-state">No tasks yet. <Link to="/employee/tasks/new">Create one</Link></div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Title</th><th>Status</th><th>Priority</th><th>Deadline</th><th>Source</th></tr>
            </thead>
            <tbody>
              {recent.map((t) => (
                <tr key={t.id}>
                  <td><Link to={`/employee/tasks/${t.id}`} className="table-link">{t.title}</Link></td>
                  <td><span className={`badge badge-${statusColor[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                  <td><span className={`badge badge-${priorityColor[t.priority]}`}>{t.priority}</span></td>
                  <td className={t.is_delayed ? 'text-danger' : ''}>{t.deadline ? fmtDate(t.deadline, true) : '—'}</td>
                  <td><span className="text-muted">{taskSourceShort(t)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <OnboardingSummary />
    </div>
  );
}
